import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MangopayReadiness } from 'src/models';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Card, Chip, Stack, Typography } from '@mui/material';

interface Props {
  readiness?: MangopayReadiness;
  onRefresh: () => void | Promise<void>;
}

export const ScaReadinessCard: FC<Props> = ({ readiness, onRefresh }) => {
  const { t } = useTranslation();

  const notSetUp = !readiness || !readiness.accountCreated;

  const checklist: { label: string; ok: boolean }[] = readiness
    ? [
        { label: t('payment.account.sca.check.accountCreated'), ok: readiness.accountCreated },
        { label: t('payment.account.sca.check.kycVerified'), ok: readiness.kycVerified },
        { label: t('payment.account.sca.check.scaEnrolled'), ok: readiness.scaEnrolled },
        { label: t('payment.account.sca.check.consentTransfer'), ok: readiness.consent.transfer },
        { label: t('payment.account.sca.check.consentRecipientRegistration'), ok: readiness.consent.recipientRegistration },
        { label: t('payment.account.sca.check.consentViewAccountInformation'), ok: readiness.consent.viewAccountInformation },
        { label: t('payment.account.sca.check.consentContactInformationUpdate'), ok: readiness.consent.contactInformationUpdate },
        { label: t('payment.account.sca.check.outflowsNotBlocked'), ok: !readiness.outflowsBlocked },
      ]
    : [];

  return (
    <Card sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {t('payment.account.sca.title')}
        </Typography>
        {!notSetUp &&
          (readiness?.isReady ? (
            <Chip icon={<CheckCircleIcon />} label={t('payment.account.sca.status.ready')} color="success" size="small" />
          ) : (
            <Chip icon={<CancelIcon />} label={t('payment.account.sca.status.actionRequired')} color="warning" size="small" />
          ))}
      </Box>

      {notSetUp ? (
        <>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
            {t('payment.account.sca.notSetUp.title')}
          </Typography>
          <Typography variant="body2" color="GrayText">
            {t('payment.account.sca.notSetUp.description')}
          </Typography>
        </>
      ) : (
        <Stack spacing={1}>
          {checklist.map((item) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center' }}>
              {item.ok ? (
                <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
              ) : (
                <CancelIcon color="error" fontSize="small" sx={{ mr: 1 }} />
              )}
              <Typography variant="body2">{item.label}</Typography>
            </Box>
          ))}
        </Stack>
      )}

      {readiness?.lastSyncedAt && (
        <Typography variant="body2" color="GrayText" sx={{ mt: 2 }}>
          {t('payment.account.sca.lastSynced', { date: new Date(readiness.lastSyncedAt).toLocaleString() })}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => onRefresh()}>
          {t('payment.account.sca.refresh')}
        </Button>
      </Box>
    </Card>
  );
};
